import { Component, DestroyRef, inject, input, output, AfterViewInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AgGridAngular } from 'ag-grid-angular';

import {
  ColDef,
  GridApi,
  GridReadyEvent,
  SelectionChangedEvent,
  CellClickedEvent,
  CellValueChangedEvent
} from 'ag-grid-community';

import { Observable, UnaryFunction, finalize, from, of } from 'rxjs';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { DeleteEventDataModel, GridDataRequest } from './api-paginated-grid.model';
import { ListResponseModel } from '@app/core/models/crud/list.model';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-api-paginated-grid',
  standalone: true,
  imports: [AgGridAngular],
  templateUrl: './api-paginated-grid.component.html',
  host: { class: 'd-flex flex-fill' }
})
export class ApiPaginatedGridComponent implements AfterViewInit {
  private _modalService = inject(NgbModal);
  private _destroyRef = inject(DestroyRef);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _pageInfo = { currentPage: 1, totalPages: 1 };

  listOperator = input<UnaryFunction<Observable<GridDataRequest>, Observable<ListResponseModel<unknown>>> | null>(null);
  createOperator = input<UnaryFunction<Observable<unknown>, Observable<void>> | null>(null);
  updateOperator = input<UnaryFunction<Observable<string>, Observable<void>> | null>(null);
  detailsOperator = input<UnaryFunction<Observable<string>, Observable<void>> | null>(null);
  deleteOperator = input<UnaryFunction<Observable<DeleteEventDataModel>, Observable<void>> | null>(null);
  submitOperator = input<UnaryFunction<Observable<unknown>, Observable<void>> | null>(null);
  cellValueChangeOperator = input<UnaryFunction<Observable<CellValueChangedEvent>, Observable<void>> | null>(null);
  editMode = input<'auto' | 'batch'>('auto');
  selectFirstRow = input<boolean>(false);
  columnDefs = input<ColDef[]>([]);

  selected = output<string | null>();
  clicked = output<CellClickedEvent>();

  created = output();
  updated = output();
  deleted = output();
  submitted = output();

  removing = false;
  cloning = false;
  submitting = false;
  rowSelected = false;

  rowSelection: 'single' | 'multiple' | undefined = 'single';
  defaultColDef = { sortable: false, filter: false, floatingFilter: false, flex: 1 };
  gridApi: GridApi | null = null;

  ngAfterViewInit(): void {
    // Overides default client pagination to use custom server side pagination
    this.overridePaginationBehavior();
  }

  gridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this._route.queryParams.subscribe((x) => {
      const { page } = x;
      this.loadData(true, page ?? 1);
    });
  }

  rowDoubleClicked() {
    if (this.updateOperator()) {
      this.update();
      return;
    }

    this.showDetails();
  }

  selectionChanged(event: SelectionChangedEvent) {
    const selectedRows = event.api.getSelectedRows();
    this.rowSelected = selectedRows.length > 0;
    selectedRows.forEach((row) => this.selected.emit(row.uid));
  }

  cellClicked(event: CellClickedEvent) {
    this.clicked.emit(event);
  }

  cellValueChanged(event: CellValueChangedEvent) {
    const cellValueChangeOperator = this.cellValueChangeOperator();
    if (!cellValueChangeOperator) return;

    of(event).pipe(cellValueChangeOperator).subscribe();
  }

  loadData(reload = true, pageNumber = 1) {
    this.gridApi?.setGridOption('loading', true);
    const listOperator = this.listOperator();
    if (!listOperator) return;

    const gridDataRequest: GridDataRequest = {
      pageInfo: {
        pageNumber,
        pageSize: 40
      },
      reload
    };

    from([gridDataRequest])
      .pipe(listOperator, takeUntilDestroyed(this._destroyRef))
      .subscribe((result) => {
        this.gridApi?.updateGridOptions({ rowData: result.data });
        this.gridApi?.setGridOption('loading', false);
        this.selected.emit(null);
        this.selectFirstGridRow();
        this.updatePaginationView(result);
      });
  }

  create() {
    const createOperator = this.createOperator();
    if (!createOperator) return;

    of(true)
      .pipe(createOperator, takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.loadData(this.isAutoEditMode());
        this.created.emit();
      });
  }

  update() {
    const updateOperator = this.updateOperator();
    if (!updateOperator) return;
    const selectedRows = this.gridApi?.getSelectedRows();
    if (!selectedRows?.length) return;

    from(selectedRows.map((row) => row.uid))
      .pipe(updateOperator, takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.loadData(this.isAutoEditMode());
        this.updated.emit();
      });
  }

  showDetails() {
    const detailsOperator = this.detailsOperator();
    if (!detailsOperator) return;
    const selectedRows = this.gridApi?.getSelectedRows();
    if (!selectedRows?.length) return;

    from(selectedRows.map((row) => row.uid))
      .pipe(detailsOperator, takeUntilDestroyed(this._destroyRef))
      .subscribe();
  }

  remove() {
    const selectedRows = this.gridApi?.getSelectedRows();
    if (!selectedRows?.length) return;

    const modalRef = this._modalService.open(ConfirmDialogComponent, { centered: true });
    modalRef.componentInstance.message = 'Are you sure you want to continue with the deletion?';

    modalRef.closed.subscribe(() => {
      const deleteOperator = this.deleteOperator();
      if (!deleteOperator) return;
      this.removing = true;

      from(selectedRows.map((row) => ({ uid: row.uid, entityVersion: row.entityVersion }) as DeleteEventDataModel))
        .pipe(
          deleteOperator,
          takeUntilDestroyed(this._destroyRef),
          finalize(() => (this.removing = false))
        )
        .subscribe(() => {
          this.loadData(this.isAutoEditMode());
          this.deleted.emit();
        });
    });
  }

  submit() {
    const submitOperator = this.submitOperator();
    if (!submitOperator) return;
    this.submitting = true;

    of(true)
      .pipe(
        submitOperator,
        takeUntilDestroyed(this._destroyRef),
        finalize(() => (this.submitting = false))
      )
      .subscribe(() => {
        this.loadData();
        this.submitted.emit();
      });
  }

  private overridePaginationBehavior() {
    const btnFirst = document.querySelector('div[data-ref="btFirst"]');
    const btnPrevious = document.querySelector('div[data-ref="btPrevious"]');
    const btnNext = document.querySelector('div[data-ref="btNext"]');
    const btnLast = document.querySelector('div[data-ref="btLast"]');

    btnFirst?.addEventListener('click', (event) => this.firstPage(event), true);
    btnPrevious?.addEventListener('click', (event) => this.previousPage(event), true);
    btnNext?.addEventListener('click', (event) => this.nextPage(event), true);
    btnLast?.addEventListener('click', (event) => this.lastPage(event), true);
  }

  private updatePaginationView(result: ListResponseModel<unknown>) {
    const labelFirstRowOnPage = document.querySelector('span[data-ref="lbFirstRowOnPage"]');
    const labelLastRowOnPage = document.querySelector('span[data-ref="lbLastRowOnPage"]');
    const labelRecordCount = document.querySelector('span[data-ref="lbRecordCount"]');
    const labelCurrent = document.querySelector('span[data-ref="lbCurrent"]');
    const labelTotal = document.querySelector('span[data-ref="lbTotal"]');

    this._pageInfo = { currentPage: result.currentPage, totalPages: result.totalPages };

    labelFirstRowOnPage!.textContent = result.firstRowOnPage.toString();
    labelLastRowOnPage!.textContent = result.lastRowOnPage.toString();
    labelRecordCount!.textContent = result.totalRecords.toString();
    labelCurrent!.textContent = result.currentPage.toString();
    labelTotal!.textContent = result.totalPages.toString();

    const btnFirst = document.querySelector('div[data-ref="btFirst"]');
    const btnPrevious = document.querySelector('div[data-ref="btPrevious"]');
    const btnNext = document.querySelector('div[data-ref="btNext"]');
    const btnLast = document.querySelector('div[data-ref="btLast"]');

    btnFirst?.classList.remove('ag-disabled');
    btnPrevious?.classList.remove('ag-disabled');
    btnNext?.classList.remove('ag-disabled');
    btnLast?.classList.remove('ag-disabled');

    if (this._pageInfo.currentPage >= this._pageInfo.totalPages) {
      btnNext?.classList.add('ag-disabled');
      btnLast?.classList.add('ag-disabled');
    }

    if (this._pageInfo.currentPage === 1) {
      btnFirst?.classList.add('ag-disabled');
      btnPrevious?.classList.add('ag-disabled');
    }
  }

  private nextPage(event: Event) {
    event.stopImmediatePropagation();

    if (this._pageInfo.currentPage < this._pageInfo.totalPages) {
      this._router.navigate([], {
        queryParams: { page: this._pageInfo.currentPage + 1 },
        queryParamsHandling: 'merge'
      });
    }
  }

  private previousPage(event: Event) {
    event.stopImmediatePropagation();
    if (this._pageInfo.currentPage > 1) {
      this._router.navigate([], {
        queryParams: { page: this._pageInfo.currentPage - 1 },
        queryParamsHandling: 'merge'
      });
    }
  }

  private firstPage(event: Event) {
    event.stopImmediatePropagation();
    if (this._pageInfo.currentPage > 1) {
      this._router.navigate([], {
        queryParams: { page: 1 },
        queryParamsHandling: 'merge'
      });
    }
  }

  private lastPage(event: Event) {
    event.stopImmediatePropagation();
    if (this._pageInfo.currentPage < this._pageInfo.totalPages) {
      this._router.navigate([], {
        queryParams: { page: this._pageInfo.totalPages },
        queryParamsHandling: 'merge'
      });
    }
  }

  private selectFirstGridRow() {
    if (!this.selectFirstRow()) return;

    const firstRowNode = this.gridApi?.getDisplayedRowAtIndex(0);

    if (firstRowNode) {
      firstRowNode.setSelected(true);
    }
  }

  private isAutoEditMode() {
    return this.editMode() === 'auto';
  }
}
